/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { StatusPill } from "@/pages/dashboard/TrainerHome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreVertical, Search, MessageCircle, Sparkles, Send, Clock, Activity, Flame } from "lucide-react";
import { formatApiError } from "@/lib/auth";
import { useSearchParams } from "react-router-dom";
import { WHATSAPP_NUMBER, useLang } from "@/lib/i18n";
import ClientActivityModal from "@/components/clients/ClientActivityModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_PRICE = { essentiel: 5, pro: 15, domaine: 0 };

function daysSince(iso) {
  if (!iso) return null;
  try {
    const last = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - last) / (1000 * 60 * 60 * 24));
    return diff;
  } catch {
    return null;
  }
}

function ActivityCell({ client, t }) {
  const iso = client.last_login_at;
  const days = daysSince(iso);
  if (days === null) {
    return <span className="text-[#A8AFA1] text-xs">{t.app.activity?.never || "Jamais"}</span>;
  }
  const label =
    days <= 0
      ? t.app.activity?.today || "Aujourd'hui"
      : days === 1
      ? t.app.activity?.yesterday || "Hier"
      : (t.app.activity?.days_ago || "Il y a {n}j").replace("{n}", days);
  const isInactive = days >= 3 && client.is_trial;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[#1C3A27] text-sm">{label}</span>
      {isInactive && (
        <span
          data-testid={`inactive-badge-${client.id}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase"
        >
          <Clock size={10} /> {t.app.activity?.inactive || "Inactif"}
        </span>
      )}
    </div>
  );
}

export default function ClientsList({ scope = "trainer" }) {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const signupFilter = searchParams.get("filter") === "signup";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [activityClientId, setActivityClientId] = useState(null);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { withCredentials: true });
      setClients(data);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleStatus = async (clientId, status) => {
    try {
      await axios.patch(`${API}/admin/clients/${clientId}/status`, { status }, { withCredentials: true });
      toast.success(t.app.clients_list.status_updated);
      fetchClients();
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  const handleSendReminder = async (clientId, clientName) => {
    try {
      const { data } = await axios.post(
        `${API}/owner/clients/${clientId}/send-reminder`,
        {},
        { withCredentials: true }
      );
      const desc = data.mocked
        ? (t.app.activity?.reminder_sent_mocked || "Relance enregistrée (mode test, sans Twilio).")
        : (t.app.activity?.reminder_sent || "Relance envoyée à {name}.").replace("{name}", clientName);
      toast.success(t.app.activity?.reminder_sent_title || "Relance envoyée", { description: desc });
      fetchClients();
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  const filtered = clients.filter((c) => {
    if (signupFilter && !(c.self_signup && c.status === "pending")) return false;
    const q = search.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  const allowedRoles = scope === "owner" ? ["owner"] : ["trainer"];

  return (
    <DashboardShell allowedRoles={allowedRoles}>
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            {signupFilter ? t.app.clients_list.overline_signup : scope === "owner" ? t.app.clients_list.overline_owner : t.app.clients_list.overline_trainer}
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
            {signupFilter ? t.app.clients_list.title_signup : t.app.clients_list.title_clients} ({filtered.length})
          </h1>
          {signupFilter && (
            <button onClick={() => setSearchParams({})} data-testid="clear-signup-filter" className="mt-2 text-sm font-semibold text-[#5C6B5C] hover:text-[#1C3A27] underline underline-offset-2">
              {t.app.clients_list.view_all}
            </button>
          )}
        </div>
        <CreateClientDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={fetchClients} />
      </header>

      <div className="mb-5 relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]" />
        <Input data-testid="clients-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.app.clients_list.search_placeholder} className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl" />
      </div>

      <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#5C6B5C]">
            {clients.length === 0 ? t.app.clients_list.empty_first : t.app.clients_list.empty_search}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_name}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_email}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_plan}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_billing}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_status}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.activity?.th_last_login || "Dernière connexion"}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_rate}</th>
                  <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const base = PLAN_PRICE[c.plan] || 0;
                  const monthlyAfter = c.billing_cycle === "annual" ? base * 0.8 : base;
                  return (
                    <tr
                      key={c.id}
                      data-testid={`client-row-${c.id}`}
                      onClick={() => setActivityClientId(c.id)}
                      className="border-t border-[#E2E4DF] cursor-pointer hover:bg-[#FAFAF6] transition-colors"
                      title="Voir le profil d'activité"
                    >
                      <td className="px-5 py-4 font-medium text-[#1C3A27]">
                        <div className="flex items-center gap-2 flex-wrap">
                          {c.name}
                          {c.self_signup && (
                            <span data-testid={`self-signup-badge-${c.id}`} title={t.app.clients_list.self_signup_title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E05A3D]/15 text-[#E05A3D] text-[10px] font-bold uppercase">
                              <Sparkles size={10} /> {t.app.clients_list.self_signup_badge}
                            </span>
                          )}
                          {(c.monthly_activity_count || 0) >= 20 && (
                            <span
                              data-testid={`very-active-badge-${c.id}`}
                              title={`${c.monthly_activity_count} actions sur 30 jours`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#E05A3D] to-[#C84B31] text-white text-[10px] font-bold uppercase shadow-sm"
                            >
                              <Flame size={10} /> Très actif
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[#5C6B5C]">{c.email}</td>
                      <td className="px-5 py-4 capitalize">{c.plan}</td>
                      <td className="px-5 py-4 capitalize">{c.billing_cycle === "annual" ? t.app.clients_list.billing_annual : t.app.clients_list.billing_monthly}</td>
                      <td className="px-5 py-4"><StatusPill status={c.status} /></td>
                      <td className="px-5 py-4"><ActivityCell client={c} t={t} /></td>
                      <td className="px-5 py-4 font-semibold text-[#1C3A27]">{monthlyAfter > 0 ? `${monthlyAfter.toFixed(1)} DT/${t.app.client.monthly}` : "—"}</td>
                      <td className="px-5 py-4 text-end" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            data-testid={`view-activity-${c.id}`}
                            onClick={() => setActivityClientId(c.id)}
                            className="p-2 rounded-lg hover:bg-[#E05A3D]/10 text-[#E05A3D]"
                            title="Voir l'activité"
                          >
                            <Activity size={16} />
                          </button>
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`client-actions-${c.id}`} className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#5C6B5C]">
                              <MoreVertical size={18} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {c.status !== "active" && (
                              <DropdownMenuItem onClick={() => handleStatus(c.id, "active")} data-testid={`activate-${c.id}`}>
                                {t.app.clients_list.action_activate}
                              </DropdownMenuItem>
                            )}
                            {c.status !== "suspended" && (
                              <DropdownMenuItem onClick={() => handleStatus(c.id, "suspended")} data-testid={`suspend-${c.id}`}>
                                {t.app.clients_list.action_suspend}
                              </DropdownMenuItem>
                            )}
                            {c.status !== "pending" && (
                              <DropdownMenuItem onClick={() => handleStatus(c.id, "pending")}>
                                {t.app.clients_list.action_pending}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem asChild>
                              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="flex items-center">
                                <MessageCircle size={14} className="me-2" />
                                {t.app.clients_list.action_whatsapp}
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`send-reminder-${c.id}`}
                              onClick={() => handleSendReminder(c.id, c.name)}
                            >
                              <Send size={14} className="me-2" />
                              {t.app.activity?.send_reminder || "Envoyer une relance"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientActivityModal
        open={Boolean(activityClientId)}
        onOpenChange={(o) => { if (!o) setActivityClientId(null); }}
        clientId={activityClientId}
      />
    </DashboardShell>
  );
}

function CreateClientDialog({ open, onOpenChange, onCreated }) {
  const { t } = useLang();
  const empty = { name: "", email: "", password: "", plan: "essentiel", billing_cycle: "monthly", status: "active" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/clients`, form, { withCredentials: true });
      toast.success(t.app.clients_list.client_created, { description: t.app.clients_list.client_created_desc.replace("{name}", form.name) });
      setForm(empty);
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(t.toasts.error_title, { description: formatApiError(err?.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const base = PLAN_PRICE[form.plan] || 0;
  const annualPrice = base * 12 * 0.8;

  const PLANS = [
    { value: "essentiel", label: `Essentiel — 5 DT/${t.app.client.monthly}` },
    { value: "pro", label: `Pro — 15 DT/${t.app.client.monthly}` },
    { value: "domaine", label: `Domaine — ${t.pricing.on_quote}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="open-create-client" className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold">
          <Plus size={18} className="me-2" /> {t.app.clients_list.new_client}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t.app.clients_list.create_title}</DialogTitle>
          <DialogDescription>{t.app.clients_list.create_desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} data-testid="create-client-form" className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2">
            <Label htmlFor="cl-name">{t.app.clients_list.full_name}</Label>
            <Input id="cl-name" data-testid="cl-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 bg-white" />
          </div>
          <div>
            <Label htmlFor="cl-email">{t.app.clients_list.email}</Label>
            <Input id="cl-email" data-testid="cl-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} className="mt-1.5 h-11 bg-white" />
          </div>
          <div>
            <Label htmlFor="cl-password">{t.app.clients_list.initial_password}</Label>
            <Input id="cl-password" data-testid="cl-password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t.app.clients_list.min6} className="mt-1.5 h-11 bg-white" />
          </div>
          <div>
            <Label>{t.app.clients_list.th_plan}</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger data-testid="cl-plan" className="mt-1.5 h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t.app.clients_list.billing}</Label>
            <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
              <SelectTrigger data-testid="cl-billing" className="mt-1.5 h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">{t.app.clients_list.billing_monthly}</SelectItem>
                <SelectItem value="annual">{t.app.clients_list.annual_minus_20}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>{t.app.clients_list.initial_status}</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="cl-status" className="mt-1.5 h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t.app.clients_list.status_active_paid}</SelectItem>
                <SelectItem value="pending">{t.app.clients_list.status_pending_paid}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {base > 0 && (
            <div className="md:col-span-2 rounded-xl bg-[#F0F0EB] p-4 text-sm">
              <div className="text-[#5C6B5C]">{t.app.clients_list.tarif_summary}</div>
              <div className="mt-1 font-semibold text-[#1C3A27]">
                {form.billing_cycle === "annual"
                  ? t.app.clients_list.tarif_annual.replace("{a}", annualPrice.toFixed(0)).replace("{b}", (base * 12).toFixed(0)).replace("{c}", ((base * 12) - annualPrice).toFixed(0))
                  : t.app.clients_list.tarif_monthly.replace("{a}", base.toFixed(0))}
              </div>
            </div>
          )}
          <DialogFooter className="md:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t.app.common.cancel}</Button>
            <Button type="submit" data-testid="cl-submit" disabled={submitting} className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">
              {submitting ? t.app.common.creating : t.app.clients_list.create_btn}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
